import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import {
  BankInquiryResult,
  CreateLinkTokenResponse, GetOrderResponse,
  GetOrdersPagedResponse,
  GetOrdersResponse,
  PayloadTransactionSummary,
  PortalNotification,
  PortalNotificationReference,
  PortalUpload
} from '../types';
import { apiRoutes } from './apiRoutes';
import { useOptimisticUpdateNotificationAndOrderNotification } from './optimisticUpdates';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useOrganizationUnsafe } from '../components/OrganizationProvider';
import { getScopes, getAudience } from './auth0Utils';
import { auth0QueryFunction } from './apiFunctions/queryFunctions/auth0QueryFunction';
import { pinQueryFunction } from './apiFunctions/queryFunctions/pinQueryFunction';
import { usePinTokenState } from '../components/context/pin-state/PinState';
import { useIsPinPage } from './useIsPinPage';
import { CsError } from './CsError';
import { RetrieveOrderRequestBody } from '../types/orderRequest';

type ApiQueryKey = readonly [string, ...readonly string[]] 

type ApiOptionsType<TQueryFnData, TData = TQueryFnData> = Omit<UseQueryOptions<TQueryFnData, CsError, TData, ApiQueryKey>, 'queryKey' | 'queryFn'>

export function useGetOrder(orderKey: string, search: string, queryOptions?: ApiOptionsType<GetOrderResponse>) {
  const cleanSearch = (() => {
    const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
    params.delete('component')
    const query = params.toString()
    return query ? `?${query}` : ''
  })()
  const response = useApiQuery<GetOrderResponse>(apiRoutes.getOrder(orderKey, cleanSearch),
    {
      enabled: !!orderKey, 
      useErrorBoundary: true, 
      ...queryOptions
    })

  const [searchParams, setSearchParams] = useSearchParams()
  const isPinPage = useIsPinPage()

  // Add/change role param if there is none or if the current query param doesn't match.
  useEffect(() => {
    if (!response.error && response.data?.userMappingInfo?.role) {
      const currentRoleParam = searchParams.get('role')

      if (response.data.userMappingInfo?.role !== currentRoleParam) {
        if (isPinPage) {
          searchParams.set('role', 'AdHoc')
          setSearchParams(searchParams)
        } else {
          searchParams.set('role', response.data.userMappingInfo.role)
          setSearchParams(searchParams)
        }
      }
    }

  }, [response, searchParams, setSearchParams, isPinPage])


  return response
}

export function useGetNotification({ notificationId, queryOptions }: { notificationId?: string; queryOptions?: ApiOptionsType<PortalNotification>; } = {}) {
  const { closingId: orderKey, id } = useParams()
  const { search } = useLocation()
  const notificationIdFinal = notificationId ?? id
  if (!orderKey || !notificationIdFinal) {
    throw new Error('useGetNotification: orderKey and notificationId are required')
  }
  const { optimisticUpdateNotificationAndOrderNotification } = useOptimisticUpdateNotificationAndOrderNotification()
  return useApiQuery<PortalNotification>(apiRoutes.getNotification(orderKey, notificationIdFinal, search), {
    onSuccess: () => {
      optimisticUpdateNotificationAndOrderNotification({ orderKey, notificationId: notificationIdFinal, search, patch: { ReadReceipt: true } })
    },
    ...queryOptions
  })
}

export function useGetPayloadMetaData(orderKey: string, notificationId: string, search: string, queryOptions?: ApiOptionsType<PayloadTransactionSummary[]>) {
  return useApiQuery<PayloadTransactionSummary[]>(apiRoutes.getPayloadMetaData(orderKey, notificationId, search), queryOptions)
}

export function useGetNotifications(orderKey: string, search: string, queryOptions?: ApiOptionsType<PortalNotificationReference[]>) {
  return useApiQuery<PortalNotificationReference[]>(apiRoutes.getOrderNotifications(orderKey, search), queryOptions)
}

export function useGetNotificationStatusesV2(orderKey: string, notificationKey: string, search: string, queryOptions?: ApiOptionsType<Partial<PortalNotification>>) {
  return useApiQuery<Partial<PortalNotification>>(apiRoutes.getNotificationStatusesV2(orderKey, notificationKey, search), { refetchOnWindowFocus: true, ...queryOptions })
}

export function useGetUploads(companyIdOrderIdHash: string, search: string, queryOptions?: ApiOptionsType<PortalUpload[]>) {
  return useApiQuery<PortalUpload[]>(apiRoutes.getUploads(companyIdOrderIdHash, search), {
    ...queryOptions,
    refetchOnWindowFocus: true,
  })
}

export function useGetUploadsForNotificationAndUserOrderKey(companyIdOrderIdHash: string, notificationId: string, search: string, queryOptions?: ApiOptionsType<PortalUpload[]>) {
  return useApiQuery<PortalUpload[]>(apiRoutes.getNotificationAndUserOrderKeyUploads(companyIdOrderIdHash, notificationId, search), {
    ...queryOptions,
    refetchOnWindowFocus: true,
  })
}

export function useCreateLinkToken(orderKey: string, notificationId: string, search: string, queryOptions?: ApiOptionsType<CreateLinkTokenResponse>) {
  return useApiQuery<CreateLinkTokenResponse>(apiRoutes.createLinkToken(orderKey, notificationId, search), {
    ...queryOptions
  })
}

export function useFormRetrieve(orderKey: string, notificationId: string, formKey: string, search: string, queryOptions?: ApiOptionsType<any>) {
  return useApiQuery<any>(apiRoutes.formRetrieve(orderKey, notificationId, formKey, search), {
    ...queryOptions
  })
}

export function useGetBankInformation(orderKey: string, notificationId: string, routingNumber: string, search: string, queryOptions?: ApiOptionsType<BankInquiryResult>) {
  return useApiQuery<BankInquiryResult>(
    apiRoutes.bankInformation(orderKey, notificationId, routingNumber, search), {
      ...queryOptions
    }
  )
}

export function useGetCustomFields(orderKey: string, search: string, queryOptions?: ApiOptionsType<{ USBankRoutingVerification: boolean }>) {
  return useApiQuery<{ USBankRoutingVerification: boolean }>(
    apiRoutes.getCustomFields(orderKey, search), {
      staleTime: Infinity,
      ...queryOptions
    }
  )
}

export function useGetOrders(queryOptions?: ApiOptionsType<GetOrdersResponse>) {
  return useApiQuery<GetOrdersResponse>(apiRoutes.getOrders(), {
    ...queryOptions
  })
}

export function useGetOrdersNew(searchParams?: URLSearchParams, queryOptions?: ApiOptionsType<GetOrdersPagedResponse>) {
  const queryKeyAddons: string[] = searchParams ? Array.from(searchParams.values()) : []
  return useApiQuery<GetOrdersPagedResponse>(apiRoutes.getOrders(searchParams), {
    ...queryOptions
  }, queryKeyAddons)
}

export function useGetOrderRequests(queryOptions?: ApiOptionsType<{ OrderRequests: RetrieveOrderRequestBody[] }>) {
  return useApiQuery<{ OrderRequests: RetrieveOrderRequestBody[] }>(apiRoutes.getOrderRequests(), queryOptions)
}

export function useApiQuery<TResponse, TResult = TResponse>(
  path: string,
  queryOptions?: ApiOptionsType<TResponse, TResult>,
  queryKeyAddon?: string[] 
) {
  const isPinPage = useIsPinPage()

  const { getAccessTokenSilently, loginWithRedirect } = useAuth0()
  const organization = useOrganizationUnsafe()
  const scope = getScopes(organization?.MfaRequired)
  const audience = getAudience(organization?.MfaRequired)
  const { pinToken, hasVerifiedPin } = usePinTokenState()

  const queryKey: ApiQueryKey = queryKeyAddon && Array.isArray(queryKeyAddon) ? [path, ...queryKeyAddon] : [path]

  const STALE_TIME_20S = 1000 * 20
  return useQuery<TResponse, CsError, TResult, ApiQueryKey>({
    staleTime: STALE_TIME_20S,
    refetchOnWindowFocus: false,
    ...queryOptions,
    enabled: enabledQuery(isPinPage, hasVerifiedPin, queryOptions?.enabled),
    useErrorBoundary: (error, query) => {

      if (error.cause === 'no_pin_token') {
        return true
      }

      const fallback = false

      // If user provided a useErrorBoundary option
      if (typeof queryOptions?.useErrorBoundary === 'function') {
        return queryOptions.useErrorBoundary(error, query)
      }
      if (typeof queryOptions?.useErrorBoundary === 'boolean') {
        return queryOptions.useErrorBoundary
      }
      return fallback
    },
    queryKey,
    queryFn: () => {
      if (isPinPage) {
        return pinQueryFunction<TResponse>(path, pinToken)
      } else {
        return auth0QueryFunction<TResponse>(path, getAccessTokenSilently, loginWithRedirect, audience, scope)
      }
    }
  })
}

export function enabledQuery(isPinQuery: boolean, hasVerifiedPin: boolean, enabledAlready?: boolean) {
  if (enabledAlready === false) {
    return false
  }

  return isPinQuery ? hasVerifiedPin : true
}
